import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog, DialogActions, DialogContent, TextInput, Typography, } from '@guildeducationinc/recess';
import { FieldOptionsLookup } from '@src/components/FieldOptionsLookup';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { useCurrentClientConfig } from '@src/hooks/useCurrentClientConfig';
import { useCurrentCurrency } from '@src/hooks/useCurrentCurrency';
import { EntityType, PaymentMethod, PaymentRequestItemType, PreimbursementStatus, RuleType, } from '@src/types';
import { isFutureDateInTimezone } from '@src/utilities/dateHelper';
import { Currency, DatePicker, MuiSingleSelect, TrackableButton, useCurrencyConversion, useLocale, } from 'pe-core-frontend';
import { useContext, useEffect, useMemo, useState } from 'react';
import styles from './EditExpenseModal.module.scss';
export const EditExpenseModal = ({ onClose, item, onChange, errors, onValidate, onSubmit, paymentRequest, }) => {
    const { t } = useLocale();
    const { getPolicy, program, setPaymentRequest } = useContext(ProgramContext);
    useEffect(() => {
        setPaymentRequest(paymentRequest);
    }, [paymentRequest]);
    const expensePolicy = getPolicy(RuleType.EXPENSE, program);
    const allowFeeTypes = expensePolicy?.allowFeeTypes !== false;
    const currencyDefault = useCurrentCurrency({ clientId: item.clientId });
    const { clientConfig } = useCurrentClientConfig();
    const collectReceiptDatesForAllTransactions = clientConfig?.extraData?.collectReceiptDatesForAllTransactions === true;
    // Check if receipt date should be collected based on payment method
    const shouldCollectReceiptDate = collectReceiptDatesForAllTransactions &&
        (paymentRequest.paymentMethod === PaymentMethod.REIMBURSEMENT ||
            (paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
                paymentRequest.preimbursementStatus === PreimbursementStatus.PENDING_PROOF));
    const isCourse = item.type === PaymentRequestItemType.COURSE;
    const isFee = item.type === PaymentRequestItemType.FEE;
    const isScholarship = item.type === PaymentRequestItemType.SCHOLARSHIP;
    const isSingleExpense = program?.isSingleExpense === true;
    // Build currency options from the Currency enum
    const [selectedCurrency, setSelectedCurrency] = useState(currencyDefault);
    const [currencyInput, setCurrencyInput] = useState(currencyDefault);
    // const [showAmountInput, setShowAmountInput] = useState(false);
    const [purchaseDate, setPurchaseDate] = useState(item.receiptDate);
    // Sync purchaseDate when item.receiptDate changes
    useEffect(() => {
        setPurchaseDate(item.receiptDate);
    }, [item.receiptDate]);
    const amountToConvert = Number(item.amountRequested) || 0;
    /**
     * Helper function to validate if a date string represents a valid date
     * @param dateStr - The date string to validate
     * @returns true if the date is valid, false otherwise
     */
    const isValidDateString = (dateStr) => {
        if (!dateStr)
            return false;
        if (!dateStr || dateStr === 'Invalid Date') {
            return false;
        }
        const date = new Date(dateStr);
        return !isNaN(date.getTime());
    };
    // For single expense programs, use program.startDate as the receipt date for conversion
    const effectiveReceiptDateForConversion = isSingleExpense ? program?.startDate : purchaseDate;
    const isValidConversionSpec = !!effectiveReceiptDateForConversion &&
        isValidDateString(effectiveReceiptDateForConversion) &&
        !!selectedCurrency &&
        !!currencyDefault &&
        amountToConvert > 0 &&
        selectedCurrency !== currencyDefault;
    const conversionSpec = useMemo(() => ({
        receiptDate: effectiveReceiptDateForConversion || '',
        baseCurrency: selectedCurrency || currencyDefault,
        quoteCurrency: currencyDefault,
        amountToConvert,
    }), [effectiveReceiptDateForConversion, selectedCurrency, currencyDefault, amountToConvert]);
    const shouldFetchConversion = !!effectiveReceiptDateForConversion &&
        !!selectedCurrency &&
        !!currencyDefault &&
        amountToConvert > 0 &&
        selectedCurrency !== currencyDefault;
    const { data: conversionData, loading: conversionLoading, error: conversionError, } = useCurrencyConversion(conversionSpec, !shouldFetchConversion);
    const showConversionFields = (selectedCurrency !== undefined &&
        selectedCurrency !== currencyDefault &&
        selectedCurrency !== '') ||
        shouldCollectReceiptDate;
    // For single expense programs, hide the purchase date input field
    const showPurchaseDateField = showConversionFields && !isSingleExpense;
    const handleChange = (field) => (e) => {
        onChange({ ...item, [field]: e.currentTarget.value });
    };
    const handleFeeChange = (opt) => {
        onChange({
            ...item,
            feeTypeId: opt?.value ? Number(opt?.value) : undefined,
            feeTypeName: opt?.label || '',
        });
    };
    const handleNumberChange = (field) => (e) => {
        onChange({ ...item, [field]: e.target.value ? e.target.value : '' });
    };
    const handleSubmit = () => {
        // For single expense programs, use program.startDate as the receipt date
        const effectiveReceiptDate = isSingleExpense ? program?.startDate : purchaseDate;
        // Update item with current receipt date and currency before validation
        const itemToValidate = {
            ...item,
            receiptDate: effectiveReceiptDate,
            originalAmountRequestedCurrency: selectedCurrency,
        };
        // Update item before validation
        onChange(itemToValidate);
        // Validate all fields (including receipt date)
        if (!onValidate())
            return;
        if (currencyDefault === selectedCurrency) {
            onSubmit({
                ...item,
                amountRequested: Number(item.amountRequested || '0'),
                // Include receipt date if config flag is enabled and payment method conditions are met
                // For single expense, always include the receipt date (program.startDate)
                ...((shouldCollectReceiptDate || isSingleExpense) && effectiveReceiptDate
                    ? { receiptDate: effectiveReceiptDate }
                    : {}),
            });
            return;
        }
        // If conversion is needed
        const intermediateAmount = Number(Number(item.amountRequested || '0') * (conversionData?.exchangeRates?.[0] || 0));
        const baseConversion = {
            baseAmount: Number(item.amountRequested || '0'),
            baseCurrency: selectedCurrency,
            quoteAmount: intermediateAmount,
            quoteCurrency: 'USD',
            exchangeRateDate: conversionData?.exchangeRateDate,
            exchangeRate: conversionData?.exchangeRates?.[0] || 0,
        };
        // If the target currency is USD, only include the first conversion object
        const conversionCalculations = currencyDefault === 'USD'
            ? [baseConversion]
            : [
                baseConversion,
                {
                    baseAmount: intermediateAmount,
                    baseCurrency: 'USD',
                    quoteAmount: Number(Number(intermediateAmount || '0') * (conversionData?.exchangeRates?.[1] || 0)),
                    targetCurrency: currencyDefault || '',
                    exchangeRateDate: conversionData?.exchangeRateDate,
                    exchangeRate: conversionData?.exchangeRates?.[1] || 0,
                },
            ];
        onSubmit({
            ...item,
            amountRequested: Number(conversionData?.convertedAmount || item.amountRequested || '0'),
            originalAmountRequested: Number(item.amountRequested || '0'),
            originalAmountRequestedCurrency: selectedCurrency,
            receiptDate: effectiveReceiptDate,
            exchangeRate: conversionData?.totalExchangeRate,
            exchangeRateDate: conversionData?.exchangeRateDate,
            currencyCalculations: conversionCalculations,
        });
    };
    const handleCurrencyChange = (currencyCode) => {
        setSelectedCurrency(currencyCode);
    };
    const title = t(item.id ? 'Edit {item}' : 'Add {item}', {
        values: { item: t(`paymentRequestItemType.${item.type}`) },
    });
    const isFutureDate = useMemo(() => {
        if (!effectiveReceiptDateForConversion)
            return false;
        const clientTimezone = clientConfig?.timezone || 'UTC';
        return isFutureDateInTimezone(effectiveReceiptDateForConversion, clientTimezone);
    }, [effectiveReceiptDateForConversion, clientConfig?.timezone]);
    const currencyOptions = Object.entries(Currency)
        .map(([code, name]) => ({
        value: code,
        label: `${code} - ${t(`currency.${name}`)}`,
    }))
        .sort((a, b) => a.value.localeCompare(b.value));
    const filteredCurrencyOptions = useMemo(() => {
        return currencyOptions.filter(option => !currencyInput ||
            option.label.toLowerCase().indexOf(currencyInput?.toLowerCase() || '') !== -1);
    }, [currencyOptions, currencyInput]);
    return (_jsxs(Dialog, { isOpen: true, title: title, onClose: onClose, fullScreen: true, forceDisableFocusTrap: true, children: [_jsxs(DialogContent, { className: styles.container, children: [isCourse && (_jsx(Typography, { variant: 'body1', children: t('Tuition payment and details for each course taken within the academic period of this request') })), _jsx(Typography, { variant: 'body1', children: t('* Required') }), isFee && allowFeeTypes && (_jsx(_Fragment, { children: _jsx(FieldOptionsLookup, { clientId: item.clientId, required: true, label: 'Fee Type', entityType: EntityType.PAYMENT_REQUEST_ITEM, fieldCode: 'fee_type_id', showAllAtOnce: true, value: (item.feeTypeId || '').toString(), onChange: handleFeeChange, errors: errors?.feeTypeId }) })), _jsx(TextInput, { label: t('Title'), required: true, value: item.title || '', onChange: handleChange('title'), errors: errors?.title }), isCourse && (_jsx(_Fragment, { children: _jsx(TextInput, { label: t('Course Code'), value: item.courseCode || '', required: true, onChange: handleChange('courseCode'), errors: errors?.courseCode }) })), _jsx("div", { className: styles.amountRow, children: _jsxs(_Fragment, { children: [_jsx(TextInput, { className: styles.amountInput, label: t('Amount'), required: true, value: item.amountRequested || '', onChange: handleNumberChange('amountRequested'), errors: errors?.amountRequested }), _jsx(MuiSingleSelect, { label: 'Currency', className: styles.currencyDropdown, value: selectedCurrency || '', onChange: e => {
                                        handleCurrencyChange(e.target.value);
                                        setCurrencyInput(e.target.value);
                                    }, options: filteredCurrencyOptions, showSearchIcon: true, onSearch: setCurrencyInput, inputValue: currencyInput, required: true, getOptionSelectedLabel: option => String(option.value) })] }) }), showConversionFields && (_jsxs(_Fragment, { children: [showPurchaseDateField && (_jsx("div", { className: styles.purchaseDateRow, children: _jsx(DatePicker, { label: isScholarship
                                        ? `${t('Date awarded (mm/dd/yyyy)')}${selectedCurrency !== currencyDefault ? ' *' : ''}`
                                        : `${t('Purchase date (mm/dd/yyyy)')}${selectedCurrency !== currencyDefault ? ' *' : ''}`, value: purchaseDate || '', onChange: date => {
                                        setPurchaseDate(date);
                                        onChange({ ...item, receiptDate: date });
                                    }, className: styles.purchaseDateInput, errors: errors?.receiptDate }) })), isValidConversionSpec && !isFutureDate && (_jsxs("div", { className: styles.convertedBox, children: [_jsxs("div", { className: styles.convertedLabel, children: [t('Amount converted to'), " ", currencyDefault, " *"] }), conversionLoading && _jsx("div", { children: t('Loading...') }), conversionError && _jsx("div", { style: { color: 'red' }, children: t('Conversion error') }), conversionData && (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.convertedSubLabel, children: [t('Exchange rate on date of purchase was'), ":", ' ', Number(conversionData.totalExchangeRate).toFixed(2)] }), _jsxs("div", { className: styles.convertedAmount, children: [Number(conversionData.convertedAmount).toFixed(2), " ", currencyDefault] })] }))] }))] }))] }), selectedCurrency === '' && (_jsx("div", { className: styles.currencyWarning, children: t('Please select a currency before submitting.') })), _jsxs(DialogActions, { fullScreen: true, children: [_jsx(TrackableButton, { enText: 'Cancel', tag: 'edit_expense_modal', appearance: 'outline', onClick: onClose, children: t('Cancel') }), _jsx(TrackableButton, { enText: 'Add', tag: 'edit_expense_modal', onClick: handleSubmit, disabled: selectedCurrency === '', children: t('Add') })] })] }));
};
